// src/features/boxDetailsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "../app/store";
import { Box, BoxVideo, Item } from "../utils/boxTypes";
import axios from "axios";
import { handleLogoutUser } from "../utils/utility";
import { useNavigate } from "react-router-dom";
import { getVipBoxes } from "./vipSlice";
import User from "../utils/user";
import { stat } from "fs";
import { fetchReceivedGiftBoxes } from "./receivedBoxesSlice";
import { getBalance } from "keycloakauthmodule";

interface BoxDetailsState {
  selectedBox: Box | null;
  selectedBoxId: string | null;
  loading: boolean;
  isLoadingWinningItem: boolean;
  error: string | null | undefined;
  winningItemsRequestStatus: "idle" | "loading" | "succeeded" | "failed";
  winningItemsRequestError: string | null;
  winningItems: Item[] | null;
  winningVipItem: Item | null;
  boxVideos: BoxVideo[] | null;
  statusForValidateUser: "idle" | "loading" | "succeeded" | "failed";
  validatedUser: User | null; // Add this for validated user
  validateUserError: string | null;
  statusForSendGift: "idle" | "loading" | "succeeded" | "failed";
  sendGiftMessage: string | null; // Add this for validated user
  sendGiftError: string | null;
}

const initialState: BoxDetailsState = {
  selectedBox: null,
  selectedBoxId: null,
  loading: false,
  isLoadingWinningItem: false,
  error: null,
  winningItems: null,
  winningVipItem: null,
  boxVideos: null,
  validatedUser: null,
  statusForValidateUser: "idle",
  validateUserError: null,
  statusForSendGift: "idle",
  sendGiftMessage: null,
  sendGiftError: null,
  winningItemsRequestStatus: "idle",
  winningItemsRequestError: null,
};

// Thunk to fetch box details from the API
export const fetchBoxDetails = createAsyncThunk<
  any, // The return type (can be more specific based on the response structure)
  string, // Argument type (boxId)
  {
    dispatch: AppDispatch; // Dispatch type
    state: RootState; // State type
    rejectValue: string; // Optional reject value type (string in this case)
  }
>(
  "boxDetails/fetchBoxDetails",
  async (boxId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const selectedBoxId = state.boxDetails.selectedBoxId;

      // Perform any necessary operations with the state
      if (selectedBoxId !== boxId) {
        // Assume `setBoxDetails` is an internal function or action you want to call
        setBoxDetails(boxId);
      }

      // Fetch the box details using axios (or your API module)
      const response = await axios.get(`/box?id=${boxId}`);
      await dispatch(fetchBoxVideos(boxId)).unwrap();

      // Return the fetched data
      return response.data;
    } catch (error: any) {
      // Check for 401 Unauthorized status
      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate()); // Call logout action
        return rejectWithValue("Unauthorized access - logging out");
      }

      // Handle error and use rejectWithValue for rejection
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue("Failed to fetch box details");
      }
    }
  }
);

// Thunk to fetch box details from the API
export const fetchBoxVideos = createAsyncThunk<
  any, // The return type (can be more specific based on the response structure)
  string, // Argument type (boxId)
  {
    dispatch: AppDispatch; // Dispatch type
    state: RootState; // State type
    rejectValue: string; // Optional reject value type (string in this case)
  }
>(
  "boxDetails/fetchBoxVideos",
  async (boxId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const selectedBoxId = state.boxDetails.selectedBoxId;

      // Perform any necessary operations with the state
      if (selectedBoxId !== boxId) {
        // Assume `setBoxDetails` is an internal function or action you want to call
        setBoxDetails(boxId);
      }

      // Fetch the box details using axios (or your API module)
      const response = await axios.get(`/get-box-videos?box_id=${boxId}`);

      // Return the fetched data
      return response.data;
    } catch (error: any) {
      // Check for 401 Unauthorized status
      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate()); // Call logout action
        return rejectWithValue("Unauthorized access - logging out");
      }

      // Handle error and use rejectWithValue for rejection
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue("Failed to fetch box details");
      }
    }
  }
);

export const fetchItemsForWinning = createAsyncThunk<
  Item[], // Return type is now an array of Items
  { boxId: string; times: number, payment_type: string }, // Parameters type (boxId and times)
  { dispatch: AppDispatch; rejectValue: string } // ThunkAPI types
>(
  "boxDetails/fetchItemsForWinning",
  async ({ boxId, times, payment_type }, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const selectedBoxId = state.boxDetails.selectedBox?.id;

      // Use the api module instead of axios directly
      const response = await axios.post(`/box/open?id=${boxId}&times=${times}&payment_type=${payment_type}`);

      // await dispatch(getBalance()).unwrap();
      return response.data as Item[]; // Adjust the return type to an array
    } catch (error: any) {
      // Use rejectWithValue to handle the rejection

       // Handle other errors and reject with the error message
       if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail);
      } else {
        return rejectWithValue("Failed to open box");
      }
    }
  }
);

export const fetchItemsForWinningForVipBox = createAsyncThunk<
  Item, // Return type
  { boxId: string }, // Parameters type (boxId and times)
  { dispatch: AppDispatch; rejectValue: string } // ThunkAPI types
>(
  "boxDetails/fetchItemsForWinningForVipBox",
  async ({ boxId }, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const selectedBoxId = state.boxDetails.selectedBox?.id;
      // Use the api module instead of axios directly
      const response = await axios.post(`/box/open_vip?id=${boxId}`);
      dispatch(getVipBoxes());
      // dispatch(getBalance());
      return response.data as Item;
    } catch (error: any) {
      // Use rejectWithValue to handle the rejection
      return rejectWithValue(error.message);
    }
  }
);

export const fetchItemsForGiftBox = createAsyncThunk<
  Item, // Return type
  { boxId: string }, // Parameters type (boxId and times)
  { dispatch: AppDispatch; rejectValue: string } // ThunkAPI types
>(
  "boxDetails/fetchItemsForWinningForVipBox",
  async ({ boxId }, { dispatch, rejectWithValue, getState }) => {
    try {
      // Use the api module instead of axios directly
      const response = await axios.post(`/gifts/open?gift_box_id=${boxId}`);
      dispatch(fetchReceivedGiftBoxes());
      // dispatch(getBalance());
      return response.data as Item;
    } catch (error: any) {
      // Use rejectWithValue to handle the rejection
      return rejectWithValue(error.message);
    }
  }
);

export const validateEmail = createAsyncThunk<
  User, // Return type
  string, // Argument type (email)
  { rejectValue: string } // ThunkAPI reject value type
>("boxDetails/validateEmail", async (email, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      `/gifts/validate_email?email=${encodeURIComponent(email)}`
    );
    return response.data as User;
  } catch (error: any) {
    if (error.response && error.response.data) {
      // Extract error message based on response structure
      const detail = error.response.data.detail;
      if (Array.isArray(detail)) {
        return rejectWithValue(detail[0]?.msg || "Validation failed.");
      } else if (typeof detail === "string") {
        return rejectWithValue(detail);
      }
    }
    return rejectWithValue("Failed to validate email.");
  }
});

export const sendGift = createAsyncThunk<
  { message: string; gift_id: string }, // Return type
  { receiver_email: string; box_id: string; gift_class: string }, // Argument type
  {  dispatch: AppDispatch; rejectValue: string } // ThunkAPI reject value type
>(
  "gifts/sendGift",
  async ({ receiver_email, box_id, gift_class }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `/gifts/send`,
        null, // Empty body
        {
          params: {
            receiver_email,
            box_id,
            gift_class,
          },
        }
      );

      await dispatch(getBalance()).unwrap();

      return response.data as { message: string; gift_id: string };
    } catch (error: any) {
      if (error.response && error.response.data) {
        const detail = error.response.data.detail;
        if (typeof detail === "string") {
          return rejectWithValue(detail);
        }
      }
      return rejectWithValue("Failed to send gift.");
    }
  }
);

const boxDetailsSlice = createSlice({
  name: "boxDetails",
  initialState,
  reducers: {
    setBoxDetails: (state, action: PayloadAction<string | null>) => {
      state.selectedBoxId = action.payload;
    },
    setSelectedBox: (state, action: PayloadAction<Box | null>) => {
      state.selectedBox = action.payload;
    },

  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBoxDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchBoxDetails.fulfilled,
        (state, action: PayloadAction<Box>) => {
          state.selectedBox = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchBoxDetails.rejected, (state, action) => {
        // Use a type assertion to handle the type
        const castAction = action as PayloadAction<string | undefined>;
        state.loading = false;
        state.error =
          castAction.payload || "An error occurred while fetching box details.";
      })
      .addCase(fetchBoxVideos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchBoxVideos.fulfilled,
        (state, action: PayloadAction<BoxVideo[]>) => {
          state.boxVideos = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchBoxVideos.rejected, (state, action) => {
        // Use a type assertion to handle the type
        const castAction = action as PayloadAction<string | undefined>;
        state.loading = false;
        state.error =
          castAction.payload || "An error occurred while fetching box details.";
      });

    builder
      .addCase(fetchItemsForWinning.pending, (state) => {
        state.winningItemsRequestStatus = "loading";
        state.isLoadingWinningItem = true;
        state.winningItemsRequestError = null;
      })
      .addCase(
        fetchItemsForWinning.fulfilled,
        (state, action: PayloadAction<Item[]>) => {
          state.winningItemsRequestStatus = "succeeded";
          state.winningItems = action.payload;
          state.isLoadingWinningItem = false;
        }
      )
      .addCase(fetchItemsForWinning.rejected, (state, action) => {
        // Use a type assertion to handle the type
        const castAction = action as PayloadAction<string | undefined>;
        state.isLoadingWinningItem = false;
        state.winningItemsRequestStatus = "failed";
        state.winningItemsRequestError = castAction.payload ||
          "An error occurred while opening box.";
      })
      .addCase(fetchItemsForWinningForVipBox.pending, (state) => {
        state.isLoadingWinningItem = true;
        state.error = null;
      })
      .addCase(
        fetchItemsForWinningForVipBox.fulfilled,
        (state, action: PayloadAction<Item>) => {
          state.winningVipItem = action.payload;
          state.isLoadingWinningItem = false;
        }
      )
      .addCase(fetchItemsForWinningForVipBox.rejected, (state, action) => {
        // Use a type assertion to handle the type
        const castAction = action as PayloadAction<string | undefined>;
        state.isLoadingWinningItem = false;
        state.error =
          castAction.payload ||
          "An error occurred while fetching winning item for box.";
      })
      .addCase(validateEmail.pending, (state) => {
        state.statusForValidateUser = "loading";
        state.validateUserError = null;
        state.validatedUser = null;
      })
      .addCase(
        validateEmail.fulfilled,
        (state, action: PayloadAction<User>) => {
          state.statusForValidateUser = "succeeded";
          state.validatedUser = action.payload;
          state.loading = false;
        }
      )
      .addCase(validateEmail.rejected, (state, action) => {
        state.statusForValidateUser = "failed";
        const castAction = action as PayloadAction<string | undefined>;
        state.loading = false;
        state.validateUserError =
          castAction.payload || "An error occurred during validation.";
      })
      .addCase(sendGift.pending, (state) => {
        state.statusForSendGift = "loading";
        state.sendGiftMessage = null;
        state.sendGiftError = null;
      })
      .addCase(
        sendGift.fulfilled,
        (
          state,
          action: PayloadAction<{ message: string; gift_id: string }>
        ) => {
          state.statusForSendGift = "succeeded";
          state.sendGiftMessage = action.payload.message;
          state.sendGiftError = null;
        }
      )
      .addCase(sendGift.rejected, (state, action) => {
        const castAction = action as PayloadAction<string | undefined>;
        state.statusForSendGift = "failed";
        state.sendGiftError =
          castAction.payload || "An error occurred while sending the gift.";
      });
  },
});

export default boxDetailsSlice.reducer;
export const selectSelectedBox = (state: RootState) =>
  state.boxDetails.selectedBox;
export const selectSelectedBoxId = (state: RootState) =>
  state.boxDetails.selectedBoxId;
export const selectLoading = (state: RootState) => state.boxDetails.loading;
export const selectError = (state: RootState) => state.boxDetails.error;
export const { setBoxDetails, setSelectedBox } = boxDetailsSlice.actions;
